import type {
  RuleConditionsStateHistoryResult,
  RuleStepStateHistoryResult,
} from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import type { ConditionGroupV3 } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  addTagActionSchema,
  assignActionSchema,
  assignV3ActionSchema,
  autoReplyActionSchema,
  removeTagActionSchema,
  setPriorityActionSchema,
  setStatusActionSchema,
  setStatusV3ActionSchema,
  type RuleAction,
} from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  RULE_V3_ACTION_LABELS,
  RuleActionType,
  type RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { StepType } from "@redotech/redo-model/advanced-flow/steps";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import type { FlowInProgress } from "../../../../../setting/flow-editor/advanced-flow-editor/flow-editor";
import type { PartialFlowStep } from "../../../../../setting/flow-editor/advanced-flow-editor/steps";
import { AddTagAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/add-tag-action";
import { AssignAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/assign-action";
import { AssignV3Action } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/assign-v3-action";
import { AutoReplyAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/auto-reply-action";
import { RemoveTagAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/remove-tag-action";
import { SetPriorityAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/set-priority-action";
import { SetStatusAction } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/set-status-action";
import { SetStatusV3Action } from "../../../../../setting/flow-editor/advanced-flow-editor/steps/rules/actions/set-status-v3-action";
import { ConditionGroupEditorV3 } from "../../../../../setting/rules/components/condition-group-editor-v3";
import { ActionResult } from "./actions/action-result";
import * as styles from "./execution-tree-view.module.css";
import { getTriggerTypeLabel } from "./rules-triggered-event";

const noop = () => {};

const ACTION_TYPE_LABELS: Record<string, string> = {
  ...RULE_V3_ACTION_LABELS,
  [RuleActionType.SET_STATUS]: "Set status",
  [RuleActionType.ASSIGN]: "Assign to user",
};

export interface StepDetailsPanelProps {
  selectedStepId: string;
  automation: FlowInProgress;
  executed: boolean;
  triggerMetadata?: {
    triggerType?: string;
    triggeredAt?: string;
    triggerDetails?: Record<string, unknown>;
  };
  selectedResult?: RuleStepStateHistoryResult;
}

export const StepDetailsPanel = memo(function StepDetailsPanel({
  selectedStepId,
  automation,
  executed,
  triggerMetadata,
  selectedResult,
}: StepDetailsPanelProps) {
  const step: PartialFlowStep | undefined = automation.steps.find(
    (s) => s.id === selectedStepId,
  );
  if (!step) {
    return null;
  }

  const isCondition =
    step.type === StepType.RULE_CONDITIONS_V3 ||
    step.type === StepType.RULE_CONDITIONS;
  const isAction =
    step.type === StepType.RULE_ACTIONS_V3 ||
    step.type === StepType.RULE_ACTIONS;
  const isTrigger = step.type === StepType.TRIGGER;

  const triggerStep: PartialFlowStep | undefined = automation.steps.find(
    (s) => s.type === StepType.TRIGGER,
  );
  const triggerType = triggerStep?.key as string | undefined;

  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderLeftWidth="1px"
      borderStyle="solid"
      className={styles.detailsPanel}
      dir="column"
      gap="lg"
      overflowY="auto"
      p="lg"
    >
      <Flex align="center" gap="sm">
        <Text fontSize="lg" fontWeight="semibold">
          {isTrigger
            ? "Trigger"
            : isCondition
              ? "Rule Conditions"
              : isAction
                ? "Rule Actions"
                : step.type}
        </Text>
        {!executed && <RedoBadge color="gray" size="sm" text="Not executed" />}
      </Flex>

      {isTrigger && (
        <Flex dir="column" gap="md">
          <Text fontSize="sm" textColor="secondary">
            Trigger type:{" "}
            {triggerType
              ? getTriggerTypeLabel(triggerType as RuleTriggerType)
              : "unknown"}
          </Text>
          {triggerMetadata?.triggeredAt && (
            <Text fontSize="sm" textColor="secondary">
              Triggered at:{" "}
              {new Date(triggerMetadata.triggeredAt).toLocaleString()}
            </Text>
          )}
          {triggerMetadata?.triggerDetails &&
            Object.keys(triggerMetadata.triggerDetails).length > 0 && (
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" fontWeight="medium">
                  Trigger Details
                </Text>
                {Object.entries(triggerMetadata.triggerDetails).map(
                  ([key, value]) => (
                    <Flex gap="xs" key={key}>
                      <Text fontSize="xs" textColor="tertiary">
                        {key}:
                      </Text>
                      <Text fontSize="xs" textColor="secondary">
                        {String(value)}
                      </Text>
                    </Flex>
                  ),
                )}
              </Flex>
            )}
        </Flex>
      )}

      {isCondition && step.conditionGroups && (
        <ConditionGroupEditorV3
          executionData={(() => {
            const conditionResult:
              | RuleConditionsStateHistoryResult
              | undefined =
              executed && selectedResult && "results" in selectedResult
                ? selectedResult
                : undefined;
            if (!conditionResult) {
              return undefined;
            }
            return {
              passed: conditionResult.passed,
              valuesAtExecution: conditionResult.valuesAtExecution ?? {},
              // Spread into a mutable array — ConditionGroupEditorV3 expects a mutable array
              results: [...conditionResult.results],
            };
          })()}
          group={step.conditionGroups as ConditionGroupV3}
          onChange={noop}
          triggerType={triggerType as RuleTriggerType | undefined}
        />
      )}

      {isAction && step.actions && (
        <>
          <Text fontSize="sm" textColor="secondary">
            Actions configured in this step:
          </Text>
          <Flex dir="column" gap="md">
            {(step.actions as RuleAction[]).map(
              (action: RuleAction, i: number) => (
                <Flex dir="column" gap="sm" key={i}>
                  <Text fontSize="sm" fontWeight="medium">
                    {ACTION_TYPE_LABELS[action.actionType] ?? action.actionType}
                  </Text>
                  <ActionConfigEditor action={action} />
                </Flex>
              ),
            )}
          </Flex>
        </>
      )}

      {isAction &&
        executed &&
        selectedResult &&
        "actionResults" in selectedResult && (
          <>
            <Divider />
            <Text fontSize="md" fontWeight="semibold">
              Execution Results
            </Text>
            <Flex dir="column" gap="sm">
              {selectedResult.actionResults.map((r, i) => (
                <ActionResult key={i} result={r} />
              ))}
            </Flex>
          </>
        )}
    </Flex>
  );
});

const ActionConfigEditor = memo(function ActionConfigEditor({
  action,
}: {
  action: RuleAction;
}) {
  switch (action.actionType) {
    case RuleActionType.SET_STATUS:
      return (
        <SetStatusAction
          action={setStatusActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.SET_STATUS_V3:
      return (
        <SetStatusV3Action
          action={setStatusV3ActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.SET_PRIORITY:
      return (
        <SetPriorityAction
          action={setPriorityActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.ADD_TAG:
      return (
        <AddTagAction
          action={addTagActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.REMOVE_TAG:
      return (
        <RemoveTagAction
          action={removeTagActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.AUTO_REPLY:
      return (
        <AutoReplyAction
          action={autoReplyActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.ASSIGN:
      return (
        <AssignAction
          action={assignActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.ASSIGN_V3:
      return (
        <AssignV3Action
          action={assignV3ActionSchema.parse(action)}
          onChange={noop}
        />
      );
    case RuleActionType.START_AI_AGENT_EXECUTION:
      return (
        <Text fontSize="xs" textColor="tertiary">
          Triggers an AI agent response on the conversation
        </Text>
      );
    case RuleActionType.LIKE_COMMENT:
    case RuleActionType.HIDE_COMMENT:
    case RuleActionType.GENERATE_CONVERSATION_INSIGHTS:
      return null;
    default:
      assertNever(action);
  }
});
